import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import useComponentSize from '@rehooks/component-size';
import useWireToolsStore from './state-hooks';
import Like from '../tools-components/like';
import CommentToggleButton from '../tools-components/comment-toggle-button';
import Comment from '../tools-components/comment';
import getWireStatistics from '../../../services/wire/statistics-services';
import { propTypes } from '../../comments/wire-comment-components/prop-types';
import WireComment from '../../comments/wire-comment-components/mobile-variant';
import Bookmark from '../bookmark';
import dt from '../../../shared/theme';

const baseH = 40;
const Container = styled(animated.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  height: ${baseH}px;

  & > * {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  height: 40px;
  display: flex;

  > a.comment-button, button.comment-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row-reverse;
  
    > span.icon-lw {
      margin-top: -4px;
      color: ${props => dt(props).theme.bodyColor};
    }
  
    > .count {
      margin-left: 8px;
      color: ${props => dt(props).theme.bodyColor};
      font: ${props => dt(props).theme.fontWeightHeavy} 16px/19.2px ${props => dt(props).theme.fontFamilyBody};
    }
  }

  &.on-roi-page {
    > button.wire-like > span {
      color: #556572;

      &.number {
        margin-top: 1px;
        line-height: 100%;
      }
    } 

    > button.comment-button {
      border-radius: 4px;
      padding: 0 10px;
      border: 1px solid transparent;

      ${props => dt(props).theme.isLarge && `
        &:hover {
          border: 1px solid #D7DFE7;
        }
      `}

      &:hover {
        text-decoration: none;
        cursor: pointer;
      }

      span.icon-lw {
        color: #556572;
      }

      > span.count {
        color: #556572;
      }
    }

    > div.bookmark > span.icon-lw-bookmark {
      color: #556572;
    }
  }

  & > button:first-child {
    margin-right: 8px;
  }

  .bookmark {
    margin-left: auto;
  }
`;

export default function WireToolsMobile({
  wireId, bookmarked, commentProps, isOnROIPage, position,
}) {
  const commentLikeHintShown = useWireToolsStore(state => state.commentLikeHintShown);
  const [show, setShow] = React.useState(false);
  const [wire, setWire] = React.useState({
    id: wireId,
    slug: '',
    liked: false,
    bookmarked,
    likeCount: 0,
    commentCount: 0,
  });
  const commentsRef = React.useRef();
  const { height: cHeight } = useComponentSize(commentsRef);
  const styles = useSpring({
    height: show ? (baseH + cHeight + 70) : baseH,
  });

  React.useEffect(() => {
    wireId && getWireStatistics(wireId).then(res => {
      const newData = _.get(res, 'data', {});
      setWire(w => ({ ...w, ...newData }));
    });
  }, [wireId]);

  const commentButtonProps = {
    wire,
    active: show,
    onToggle: d => setShow(d),
  };

  const onMount = React.useCallback(() => {
    if (window.location.hash === '#comment_body') {
      setShow(true);
    }
  }, []);

  const wireCommentProps = {
    ...commentProps,
    showCommentLikeHint: !commentLikeHintShown && show && commentProps.showCommentLikeHint,
  };

  return (
    <Container style={styles}>
      <ButtonContainer ref={onMount} className={isOnROIPage ? 'on-roi-page' : ''}>
        <Like wire={wire} isOnROIPage={isOnROIPage} position={position} asButton />
        {!isOnROIPage
          ? <CommentToggleButton {...commentButtonProps} />
          : <Comment wire={wire} isOnROIPage={isOnROIPage} position={position} />}
        <Bookmark wire={wire} showLabel={false} isOnROIPage={isOnROIPage} position={position} />
      </ButtonContainer>
      {!isOnROIPage && (
      <WireComment
        {...wireCommentProps}
        onHeaderClicked={() => setShow(false)}
        ref={commentsRef}
      />
      )}
    </Container>
  );
}

WireToolsMobile.propTypes = {
  wireId: PropTypes.number.isRequired,
  bookmarked: PropTypes.bool.isRequired,
  asButton: PropTypes.bool,
  showLabel: PropTypes.bool,
  isOnROIPage: PropTypes.bool,
  commentProps: PropTypes.shape({
    wireSlug: PropTypes.string,
    isCurrentUserConfirmed: PropTypes.bool,
    showCommentLikeHint: PropTypes.bool.isRequired,
    comments: PropTypes.arrayOf(PropTypes.shape(propTypes)),
  }).isRequired,
  position: PropTypes.number,
};

WireToolsMobile.defaultProps = {
  asButton: false,
  showLabel: false,
  isOnROIPage: false,
  position: null,
};
